/**
 * ConnectorIcon Atom
 * Icons for different connector types and status
 */

import { Zap, Circle, AlertTriangle, HelpCircle, Ban } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { ConnectorStatus } from '../../types/dashboard-types';

interface ConnectorIconProps {
  status: ConnectorStatus;
  type?: string;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
}

const statusConfig: Record<ConnectorStatus, { icon: typeof Zap; color: string; label: string }> = {
  Available: {
    icon: Circle,
    color: 'text-green-500',
    label: 'Available',
  },
  Charging: {
    icon: Zap,
    color: 'text-blue-500',
    label: 'Charging',
  },
  Faulted: {
    icon: AlertTriangle,
    color: 'text-red-500',
    label: 'Faulted',
  },
  Unavailable: {
    icon: Ban,
    color: 'text-gray-400',
    label: 'Unavailable',
  },
  Unknown: {
    icon: HelpCircle,
    color: 'text-yellow-500',
    label: 'Unknown',
  },
};

const sizeClasses = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
};

export function ConnectorIcon({ status, size = 'md' }: ConnectorIconProps) {
  const config = statusConfig[status] || statusConfig.Unknown;
  const Icon = config.icon;

  return (
    <Icon
      className={cn(
        sizeClasses[size],
        config.color,
        status === 'Charging' && 'animate-pulse'
      )}
      title={config.label}
    />
  );
}

/**
 * Connector type icon component
 */
interface ConnectorTypeIconProps {
  type: 'CCS2' | 'CHAdeMO' | 'Type2' | 'J1772' | string;
  size?: 'sm' | 'md' | 'lg';
}

export function ConnectorTypeIcon({ type, size = 'md' }: ConnectorTypeIconProps) {
  // DC connectors (fast charging)
  const isDC = type === 'CCS2' || type === 'CHAdeMO';
  const color = isDC ? 'text-blue-600' : 'text-green-600';

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center font-semibold rounded',
        size === 'sm' && 'text-[10px] px-1 py-0.5',
        size === 'md' && 'text-xs px-1.5 py-0.5',
        size === 'lg' && 'text-sm px-2 py-1',
        color,
        isDC ? 'bg-blue-50' : 'bg-green-50'
      )}
      title={isDC ? 'DC Fast Charging' : 'AC Charging'}
    >
      {type}
    </span>
  );
}
